import { useEffect, useMemo, useRef, useState } from "react";
import { newArticleViewCopy } from "../app/i18n";
import type { AppLanguage } from "../app/language";
import { createRichTextContent, getRichTextPlainText, normalizeRichTextContent } from "../app/richText";
import { QuillEditor, type QuillEditorHandle } from "../app/QuillEditor";
import { UserAvatarBadge } from "../app/ui";
import type { KnowledgeArticle, KnowledgeArticleCreateInput, KnowledgeArticleStatus, KnowledgeArticleVisibility, KnowledgeCollection, RichTextContent, TicketCategory, WorkspaceMember } from "../app/types";
import { getInitials, resolveWorkspaceMemberByEmail } from "../app/utils";

type ArticleDraft = {
  title: string;
  summary: string;
  collectionId: string;
  visibility: KnowledgeArticleVisibility;
  sharedWithUserEmails: string[];
  audience: string;
  status: KnowledgeArticleStatus;
  isFeatured: boolean;
  estimatedReadTime: string;
  keywords: string;
  content: RichTextContent;
};

type NewArticleViewProps = {
  collections: KnowledgeCollection[];
  ticketCategories: TicketCategory[];
  teamMembers: WorkspaceMember[];
  currentUserEmail: string | null;
  language: AppLanguage;
  article?: KnowledgeArticle;
  defaultCollectionId?: string | null;
  onBack: () => void;
  onSaveArticle: (input: KnowledgeArticleCreateInput, articleId?: string) => Promise<KnowledgeArticle>;
  onSaveComplete: (article: KnowledgeArticle) => void;
};

const STORAGE_KEY = "kuii.new-article-draft";

const defaultArticleDraft: ArticleDraft = {
  title: "Troubleshoot webhook delivery drift",
  summary:
    "Document the issue, likely causes, and step-by-step recovery guidance so support can respond consistently during webhook incidents.",
  collectionId: "",
  visibility: "workspace",
  sharedWithUserEmails: [],
  audience: "",
  status: "Draft",
  isFeatured: false,
  estimatedReadTime: "7 min read",
  keywords: "webhooks, incidents, retries, delivery logs",
  content: {
    html: "<h2>When to use this article</h2><p>Use this playbook when a customer reports delayed, missing, or duplicated webhook deliveries after a release, provider incident, or retry queue spike.</p><ul><li>Confirm the affected workspace, endpoint URL, and event type.</li><li>Review recent delivery logs for latency spikes or retry storms.</li><li>Share a customer-safe status update before escalating.</li></ul><blockquote>A good article reduces response time by making the next best action obvious.</blockquote>",
  },
};

function getFallbackCollectionId(collections: KnowledgeCollection[], defaultCollectionId?: string | null) {
  if (defaultCollectionId && collections.some(collection => collection.id === defaultCollectionId)) {
    return defaultCollectionId;
  }

  return collections[0]?.id ?? "";
}

function getDefaultArticleCategory(ticketCategories: TicketCategory[]) {
  return ticketCategories[0]?.name ?? "General";
}

function getDraftFromArticle(article: KnowledgeArticle, ticketCategories: TicketCategory[]): ArticleDraft {
  return {
    title: article.title,
    summary: article.summary,
    collectionId: article.collectionId ?? "",
    visibility: article.visibility ?? "workspace",
    sharedWithUserEmails: Array.isArray(article.sharedWithUserEmails) ? article.sharedWithUserEmails : [],
    audience: article.audience || getDefaultArticleCategory(ticketCategories),
    status: article.status,
    isFeatured: article.isFeatured,
    estimatedReadTime: article.estimatedReadTime,
    keywords: article.keywords.join(", "),
    content: normalizeRichTextContent(article.content),
  };
}

function readStoredDraft(collections: KnowledgeCollection[], ticketCategories: TicketCategory[], defaultCollectionId?: string | null) {
  const defaultCategory = getDefaultArticleCategory(ticketCategories);

  if (typeof window === "undefined") {
    return {
      ...defaultArticleDraft,
      collectionId: getFallbackCollectionId(collections, defaultCollectionId),
      audience: defaultCategory,
    };
  }

  const rawValue = window.localStorage.getItem(STORAGE_KEY);

  if (!rawValue) {
    return {
      ...defaultArticleDraft,
      collectionId: getFallbackCollectionId(collections, defaultCollectionId),
      audience: defaultCategory,
    };
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<ArticleDraft> & { category?: string };
    const matchedCollectionId = parsed.collectionId
      ?? collections.find(collection => collection.name === parsed.category)?.id
      ?? getFallbackCollectionId(collections, defaultCollectionId);

    return {
      ...defaultArticleDraft,
      ...parsed,
      collectionId: matchedCollectionId,
      visibility: parsed.visibility === "private" ? "private" : "workspace",
      sharedWithUserEmails: Array.isArray(parsed.sharedWithUserEmails)
        ? parsed.sharedWithUserEmails.map(value => String(value).trim().toLowerCase()).filter(Boolean)
        : [],
      audience: typeof parsed.audience === "string" && parsed.audience.trim().length > 0 ? parsed.audience : defaultCategory,
      content: normalizeRichTextContent(parsed.content) ?? defaultArticleDraft.content,
    } satisfies ArticleDraft;
  } catch {
    return {
      ...defaultArticleDraft,
      collectionId: getFallbackCollectionId(collections, defaultCollectionId),
      audience: defaultCategory,
    };
  }
}

function getBlockCount(content: RichTextContent) {
  const html = content?.html ?? "";
  const matches = html.match(/<(p|h[1-6]|ul|ol|blockquote|pre|table|img)\b/gi);
  return matches ? matches.length : 0;
}

function getWordCount(title: string, summary: string, content: RichTextContent) {
  const text = [title, summary, getRichTextPlainText(content)].join(" ").trim();

  if (text.length === 0) {
    return 0;
  }

  return text.split(/\s+/).filter(Boolean).length;
}

function formatEstimatedReadTime(wordCount: number, language: AppLanguage) {
  if (wordCount === 0) {
    return language === "ja" ? "0 分で読了" : "0 min read";
  }

  const minutes = Math.max(1, Math.ceil(wordCount / 200));
  return language === "ja" ? `${minutes} 分で読了` : `${minutes} min read`;
}

export function NewArticleView({ collections, ticketCategories, teamMembers, currentUserEmail, language, article, defaultCollectionId, onBack, onSaveArticle, onSaveComplete }: NewArticleViewProps) {
  const copy = newArticleViewCopy[language];
  const storageKey = article ? `${STORAGE_KEY}.${article.id}` : STORAGE_KEY;
  const editorRef = useRef<QuillEditorHandle | null>(null);
  const autosaveTimerRef = useRef<number | null>(null);
  const [draft, setDraft] = useState<ArticleDraft>(() => (article ? getDraftFromArticle(article, ticketCategories) : readStoredDraft(collections, ticketCategories, defaultCollectionId)));
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [saveLabel, setSaveLabel] = useState(copy.page.autosaved);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);
  const isEditing = Boolean(article);

  const articleBlockCount = useMemo(() => getBlockCount(draft.content), [draft.content]);
  const articleWordCount = useMemo(() => getWordCount(draft.title, draft.summary, draft.content), [draft.content, draft.summary, draft.title]);
  const estimatedReadTime = useMemo(() => formatEstimatedReadTime(articleWordCount, language), [articleWordCount, language]);
  const selectedCollection = useMemo(
    () => (draft.visibility === "private" ? null : collections.find(collection => collection.id === draft.collectionId) ?? collections[0] ?? null),
    [collections, draft.collectionId, draft.visibility],
  );
  const shareableMembers = useMemo(
    () => teamMembers.filter(member => member.email.toLowerCase() !== currentUserEmail?.toLowerCase()),
    [currentUserEmail, teamMembers],
  );
  const memberNameByEmail = useMemo(() => new Map(teamMembers.map(member => [member.email.trim().toLowerCase(), member.name])), [teamMembers]);
  const canUsePrivateArticles = Boolean(currentUserEmail);
  const categoryOptions = useMemo(() => {
    const options = ticketCategories.map(category => category.name).filter(Boolean);

    if (draft.audience.trim() && !options.some(option => option.toLowerCase() === draft.audience.trim().toLowerCase())) {
      options.unshift(draft.audience.trim());
    }

    if (options.length === 0) {
      options.push(getDefaultArticleCategory(ticketCategories));
    }

    return [...new Set(options)];
  }, [draft.audience, ticketCategories]);

  useEffect(() => {
    if (article) {
      setDraft(getDraftFromArticle(article, ticketCategories));
      setSaveLabel(copy.page.loadedDraft);
      setPublishMessage(null);
      return;
    }

    setDraft(readStoredDraft(collections, ticketCategories, defaultCollectionId));
  }, [article, collections, defaultCollectionId, ticketCategories]);

  useEffect(() => {
    if (draft.visibility === "private" || collections.length === 0) {
      return;
    }

    if (!draft.collectionId || !collections.some(collection => collection.id === draft.collectionId)) {
      setDraft(currentDraft => ({
        ...currentDraft,
        collectionId: getFallbackCollectionId(collections, defaultCollectionId),
      }));
    }
  }, [collections, defaultCollectionId, draft.collectionId, draft.visibility]);

  useEffect(() => {
    if (draft.visibility !== "private") {
      return;
    }

    if (!draft.isFeatured && !draft.collectionId) {
      return;
    }

    setDraft(currentDraft => ({
      ...currentDraft,
      isFeatured: false,
      collectionId: "",
    }));
  }, [draft.collectionId, draft.isFeatured, draft.visibility]);

  useEffect(() => {
    if (draft.audience.trim()) {
      return;
    }

    setDraft(currentDraft => ({
      ...currentDraft,
      audience: getDefaultArticleCategory(ticketCategories),
    }));
  }, [draft.audience, ticketCategories]);

  const handleEditorChange = (html: string) => {
    setDraft(currentDraft => ({
      ...currentDraft,
      content: createRichTextContent(html),
    }));
    setSaveLabel(copy.page.pendingAutosave);
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
    }

    setIsSaving(true);
    autosaveTimerRef.current = window.setTimeout(() => {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          ...draft,
          estimatedReadTime,
        } satisfies ArticleDraft),
      );
      setIsSaving(false);
      setSaveLabel(copy.page.autosavedAt(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })));
    }, 500);

    return () => {
      if (autosaveTimerRef.current !== null) {
        window.clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [draft, estimatedReadTime, storageKey]);

  const updateField = <K extends keyof ArticleDraft>(field: K, value: ArticleDraft[K]) => {
    setDraft(currentDraft => ({
      ...currentDraft,
      [field]: value,
    }));
  };

  const handleVisibilityChange = (visibility: KnowledgeArticleVisibility) => {
    setDraft(currentDraft => ({
      ...currentDraft,
      visibility,
      collectionId: visibility === "private" ? "" : currentDraft.collectionId || getFallbackCollectionId(collections, defaultCollectionId),
      isFeatured: visibility === "private" ? false : currentDraft.isFeatured,
    }));
  };

  const toggleSharedUser = (email: string) => {
    const normalizedEmail = email.trim().toLowerCase();

    setDraft(currentDraft => ({
      ...currentDraft,
      sharedWithUserEmails: currentDraft.sharedWithUserEmails.includes(normalizedEmail)
        ? currentDraft.sharedWithUserEmails.filter(value => value !== normalizedEmail)
        : [...currentDraft.sharedWithUserEmails, normalizedEmail].sort((left, right) => left.localeCompare(right)),
    }));
  };

  const captureEditorContent = (): RichTextContent => {
    const html = editorRef.current?.getHTML();

    if (html === undefined) {
      return draft.content;
    }

    const nextContent = createRichTextContent(html);
    setDraft(currentDraft => ({
      ...currentDraft,
      content: nextContent,
    }));
    return nextContent;
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    const nextContent = captureEditorContent();

    if (typeof window !== "undefined") {
      const nextDraft = {
        ...draft,
        estimatedReadTime,
        content: nextContent,
      } satisfies ArticleDraft;

      window.localStorage.setItem(storageKey, JSON.stringify(nextDraft));
    }

    setIsSaving(false);
    setSaveLabel(copy.page.savedDraftAt(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })));
  };

  const handlePublish = async () => {
    setIsPublishing(true);

    try {
      const nextContent = captureEditorContent();
      const nextStatus = isEditing ? draft.status : "Published";

      const savedArticle = await onSaveArticle({
        title: draft.title.trim(),
        summary: draft.summary.trim(),
        category: draft.visibility === "private" ? draft.audience : selectedCollection?.name,
        collectionId: selectedCollection?.id ?? null,
        visibility: draft.visibility,
        ownerUserEmail: currentUserEmail,
        sharedWithUserEmails: draft.sharedWithUserEmails,
        audience: draft.audience,
        status: nextStatus,
        isFeatured: draft.isFeatured,
        estimatedReadTime,
        keywords: draft.keywords
          .split(",")
          .map(keyword => keyword.trim())
          .filter(Boolean),
        content: nextContent,
      }, article?.id);

      if (typeof window !== "undefined") {
        window.localStorage.removeItem(storageKey);
      }

      const summary = isEditing
        ? copy.page.publishUpdatedSummary(draft.title, getBlockCount(nextContent), draft.visibility === "private" ? copy.sectionLabels.private : draft.audience)
        : copy.page.publishNewSummary(draft.title, getBlockCount(nextContent), draft.visibility === "private" ? copy.sectionLabels.private : draft.audience);
      setPublishMessage(summary);
      setDraft(currentDraft => ({
        ...currentDraft,
        visibility: savedArticle.visibility,
        sharedWithUserEmails: savedArticle.sharedWithUserEmails,
        status: nextStatus,
        isFeatured: savedArticle.isFeatured,
        estimatedReadTime,
        content: nextContent,
      }));
      setSaveLabel(isEditing ? copy.page.savedToKnowledge : copy.page.publishedToKnowledge);
      onSaveComplete(savedArticle);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="brand-primary-subtle-button inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] shadow-[0_12px_24px_rgba(15,23,42,0.06)] transition-colors"
          >
            <span aria-hidden="true">←</span>
            <span>{copy.page.back}</span>
          </button>
          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow(isEditing)}</p>
          <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.page.title(isEditing)}</h1>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">
            {copy.page.description(isEditing)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-full border border-white/60 bg-white px-4 py-2 text-xs font-semibold text-[#667085] shadow-[0_12px_24px_rgba(15,23,42,0.05)]">
            {isSaving ? copy.page.saving : saveLabel}
          </div>
          <button
            type="button"
            onClick={() => void handleSaveDraft()}
            className="rounded-xl border border-[#d0d5dd] bg-white px-5 py-3 text-sm font-bold text-[#344054] shadow-[0_12px_24px_rgba(15,23,42,0.05)] transition-colors hover:bg-[#f9fafb]"
          >
            {copy.page.saveDraft}
          </button>
          <button
            type="button"
            onClick={() => void handlePublish()}
            disabled={isPublishing}
            className="brand-primary-button rounded-xl px-5 py-3 text-sm font-bold"
          >
            {isPublishing ? (isEditing ? copy.page.saving : copy.page.publishing) : isEditing ? copy.page.saveChanges : copy.page.publishArticle}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_360px]">
        <div className="space-y-6">
          <section className="rounded-[32px] border border-white/60 bg-white p-8 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div className="space-y-5">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.draft.articleTitle}</span>
                <input
                  type="text"
                  value={draft.title}
                  onChange={event => updateField("title", event.target.value)}
                  placeholder={copy.draft.articleTitlePlaceholder}
                  className="mt-3 w-full rounded-[24px] border border-[#e4e7ec] bg-[#fcfcfd] px-5 py-4 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828] outline-none transition focus:border-[#00855d] focus:bg-white"
                />
              </label>
            </div>

            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <label className="block lg:col-span-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.draft.summary}</span>
                <textarea
                  value={draft.summary}
                  onChange={event => updateField("summary", event.target.value)}
                  rows={4}
                  placeholder={copy.draft.summaryPlaceholder}
                  className="mt-3 w-full resize-none rounded-[24px] border border-[#e4e7ec] bg-[#fcfcfd] px-5 py-4 text-sm leading-6 text-[#344054] outline-none transition focus:border-[#00855d] focus:bg-white"
                />
              </label>

              <div className="rounded-[24px] border border-[#e4e7ec] bg-[#fcfcfd] p-4 lg:col-span-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.draft.accessScope}</span>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {([
                    ["workspace", copy.draft.workspaceLabel, copy.draft.workspaceDetail],
                    ["private", copy.draft.privateLabel, copy.draft.privateDetail],
                  ] as const).map(([value, label, detail]) => {
                    const active = draft.visibility === value;
                    const disabled = value === "private" && !canUsePrivateArticles;

                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          if (disabled) {
                            return;
                          }

                          handleVisibilityChange(value);
                        }}
                        disabled={disabled}
                        className={`rounded-[20px] border px-4 py-4 text-left transition ${active ? "border-[#b7dfcf] bg-[#f4fbf7]" : "border-[#e4e7ec] bg-white hover:border-[#cfd4dc]"} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                      >
                        <p className="text-sm font-semibold text-[#101828]">{label}</p>
                        <p className="mt-2 text-xs leading-5 text-[#667085]">{detail}</p>
                      </button>
                    );
                  })}
                </div>
                {!canUsePrivateArticles ? <p className="mt-3 text-xs text-[#b42318]">{copy.draft.privateNeedsUser}</p> : null}
              </div>

              {draft.visibility === "workspace" ? (
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.draft.collection}</span>
                  <select
                    value={draft.collectionId}
                    onChange={event => updateField("collectionId", event.target.value)}
                    className="mt-3 w-full rounded-2xl border border-[#e4e7ec] bg-[#fcfcfd] px-4 py-3 text-sm font-semibold text-[#101828] outline-none transition focus:border-[#00855d] focus:bg-white"
                  >
                    {collections.map(collection => (
                      <option key={collection.id} value={collection.id}>
                        {collection.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-4">
                  <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.draft.privatePlacement}</span>
                  <p className="mt-3 text-sm font-semibold text-[#101828]">{copy.draft.privatePlacementTitle}</p>
                  <p className="mt-2 text-sm leading-6 text-[#667085]">{copy.draft.privatePlacementDetail}</p>
                </div>
              )}

              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.draft.category}</span>
                <select
                  value={draft.audience}
                  onChange={event => updateField("audience", event.target.value)}
                  className="mt-3 w-full rounded-2xl border border-[#e4e7ec] bg-[#fcfcfd] px-4 py-3 text-sm font-semibold text-[#101828] outline-none transition focus:border-[#00855d] focus:bg-white"
                >
                  {categoryOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-8 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.draft.bodyEyebrow}</p>
                <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.draft.bodyTitle}</h2>
              </div>
              <div className="rounded-full bg-[#f2f4f7] px-4 py-2 text-xs font-semibold text-[#475467]">
                {isEditorReady ? copy.draft.blockCount(articleBlockCount) : copy.draft.loadingEditor}
              </div>
            </div>

            <QuillEditor
              ref={editorRef}
              preset="article"
              initialHTML={draft.content?.html ?? ""}
              placeholder={copy.draft.editorPlaceholder}
              className="quill-host quill-host--article mt-6 min-h-[34rem]"
              onChange={handleEditorChange}
              onReady={() => setIsEditorReady(true)}
              onError={() => setSaveLabel(copy.page.editorFailed)}
            />
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.meta.eyebrow}</p>
            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="text-xs font-semibold text-[#475467]">{copy.meta.status}</span>
                <select
                  value={draft.status}
                  onChange={event => updateField("status", event.target.value as KnowledgeArticleStatus)}
                  className="mt-2 w-full rounded-2xl border border-[#e4e7ec] bg-[#fcfcfd] px-4 py-3 text-sm font-semibold text-[#101828] outline-none transition focus:border-[#00855d] focus:bg-white"
                >
                  {(["Draft", "Review", "Published"] as const).map(option => (
                    <option key={option} value={option}>
                      {copy.statusOptions[option]}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-semibold text-[#475467]">{copy.meta.estimatedReadTime}</span>
                <div className="mt-2 rounded-2xl border border-[#e4e7ec] bg-[#fcfcfd] px-4 py-3">
                  <p className="text-sm font-semibold text-[#101828]">{estimatedReadTime}</p>
                  <p className="mt-1 text-xs text-[#667085]">{copy.meta.calculatedFromWords(articleWordCount)}</p>
                </div>
              </label>

              <label className="block">
                <span className="text-xs font-semibold text-[#475467]">{copy.meta.featuredPlacement}</span>
                <button
                  type="button"
                  onClick={() => {
                    if (draft.visibility === "private") {
                      return;
                    }

                    updateField("isFeatured", !draft.isFeatured);
                  }}
                  disabled={draft.visibility === "private"}
                  className={`mt-2 flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${draft.isFeatured ? "border-[#b7dfcf] bg-[#f4fbf7]" : "border-[#e4e7ec] bg-[#fcfcfd] hover:bg-white"} ${draft.visibility === "private" ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full ${draft.isFeatured ? "bg-white text-[#006949]" : "bg-[#f2f4f7] text-[#667085]"}`}>
                      <span className="material-symbols-outlined text-[18px] leading-none">{draft.isFeatured ? "keep" : "keep_off"}</span>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[#101828]">{draft.isFeatured ? copy.meta.pinned : copy.meta.notPinned}</p>
                    <p className="mt-1 text-xs text-[#667085]">
                      {draft.visibility === "private"
                        ? copy.meta.privatePinnedDetail
                        : draft.isFeatured
                          ? copy.meta.pinnedDetail
                          : copy.meta.notPinnedDetail}
                    </p>
                    </div>
                  </div>
                  <span className={`inline-flex h-6 w-11 items-center rounded-full p-1 transition ${draft.isFeatured ? "bg-[#006949] justify-end" : "bg-[#d0d5dd] justify-start"}`}>
                    <span className="h-4 w-4 rounded-full bg-white shadow-sm" />
                  </span>
                </button>
              </label>

              <label className="block">
                <span className="text-xs font-semibold text-[#475467]">{copy.meta.keywords}</span>
                <textarea
                  value={draft.keywords}
                  onChange={event => updateField("keywords", event.target.value)}
                  rows={4}
                  className="mt-2 w-full resize-none rounded-2xl border border-[#e4e7ec] bg-[#fcfcfd] px-4 py-3 text-sm leading-6 text-[#101828] outline-none transition focus:border-[#00855d] focus:bg-white"
                />
              </label>

              <div className="block">
                <span className="text-xs font-semibold text-[#475467]">{copy.meta.privateSharing}</span>
                <div className="mt-2 rounded-2xl border border-[#e4e7ec] bg-[#fcfcfd] p-4">
                  {draft.visibility === "private" ? (
                    <>
                      <p className="text-sm font-semibold text-[#101828]">{copy.meta.owner}: {memberNameByEmail.get(currentUserEmail?.toLowerCase() ?? "") ?? currentUserEmail ?? copy.meta.unknown}</p>
                      <p className="mt-1 text-xs leading-5 text-[#667085]">{copy.meta.chooseTeammates}</p>
                      {draft.sharedWithUserEmails.length > 0 ? (
                        <div className="mt-4 rounded-2xl bg-white px-4 py-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.meta.peopleWithAccess}</p>
                          <div className="mt-3 flex flex-wrap gap-3">
                            {draft.sharedWithUserEmails.map(email => {
                              const member = resolveWorkspaceMemberByEmail(teamMembers, email);

                              return (
                                <div key={email} className="flex items-center gap-2 rounded-full bg-[#f9fafb] px-2.5 py-2">
                                  <UserAvatarBadge
                                    avatarUrl={member?.avatarUrl ?? null}
                                    initials={member?.initials ?? getInitials(memberNameByEmail.get(email) ?? email)}
                                    className="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold"
                                  />
                                  <div className="pr-1">
                                    <p className="text-xs font-semibold text-[#101828]">{memberNameByEmail.get(email) ?? email}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                      {shareableMembers.length > 0 ? (
                        <div className="mt-4 space-y-2">
                          {shareableMembers.map(member => {
                            const checked = draft.sharedWithUserEmails.includes(member.email.toLowerCase());

                            return (
                              <label key={member.email} className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3 text-sm text-[#344054] shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleSharedUser(member.email)}
                                  className="mt-1 h-4 w-4 rounded border-[#d0d5dd] text-[#006949] focus:ring-[#006949]"
                                />
                                <span className="flex items-start gap-3">
                                  <UserAvatarBadge
                                    avatarUrl={member.avatarUrl ?? null}
                                    initials={member.initials}
                                    className="brand-primary-avatar flex h-10 w-10 items-center justify-center rounded-full text-[11px] font-bold"
                                  />
                                  <span>
                                    <span className="block font-semibold text-[#101828]">{member.name}</span>
                                    <span className="block text-xs text-[#667085]">{member.email}</span>
                                  </span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="mt-3 text-xs text-[#667085]">{copy.meta.noUsersToShare}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm leading-6 text-[#667085]">{copy.meta.switchToPrivate}</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.readiness.eyebrow}</p>
            <div className="mt-5 space-y-3">
              {[
                { complete: draft.title.trim().length > 0, label: copy.readiness.titleSlug },
                { complete: draft.summary.trim().length > 0, label: copy.readiness.summaryReady },
                { complete: articleBlockCount >= 3, label: copy.readiness.bodyStructured },
                {
                  complete: draft.visibility === "private" || draft.isFeatured,
                  label: draft.visibility === "private" ? copy.readiness.privateConfigured : copy.readiness.featuredConfigured,
                },
                { complete: draft.keywords.trim().length > 0, label: copy.readiness.keywords },
              ].map(({ complete, label }) => (
                <div key={label} className="flex items-center gap-3 rounded-2xl bg-[#f9fafb] px-4 py-3">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${complete ? "bg-[#dcfce7] text-[#166534]" : "bg-[#f2f4f7] text-[#667085]"}`}
                  >
                    {complete ? "✓" : "•"}
                  </span>
                  <span className="text-sm font-semibold text-[#101828]">{label}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-[linear-gradient(180deg,#0b1f18_0%,#123528_100%)] p-6 text-white shadow-[0_20px_40px_rgba(26,28,29,0.14)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/60">{copy.tips.eyebrow}</p>
            <ul className="mt-4 space-y-3 text-sm leading-6 text-white/75">
              <li>{copy.tips.headings}</li>
              <li>{copy.tips.checklists}</li>
              <li>{copy.tips.tables}</li>
            </ul>
          </section>

          {publishMessage ? (
            <section className="rounded-[32px] border border-[#cce7db] bg-[#f4fbf7] p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
              <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#027a48]">{copy.preview.eyebrow}</p>
              <p className="mt-3 text-sm leading-6 text-[#175c3b]">{publishMessage}</p>
            </section>
          ) : null}
        </aside>
      </div>
    </section>
  );
}